'use client'

import { MouseEvent, ReactNode, type JSX } from 'react'
import { Cell, Text, Checkbox, Radio, Badge } from '@vinted/web-ui'
import type { CellProps } from '@vinted/web-ui'
import { isFunction } from 'lodash'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { CatalogBadgeModel, WEB_CATALOG_ROOT_ALL_CODE } from '@marketplace-web/catalog/catalog-data'

import { List } from '@marketplace-web/common-components/list-ui'

export type SelectableListItem<T, V = string | number> = {
  id: number | string
  title: string | JSX.Element
  value: V
  data?: T
}

type ListItemClickCallback<T, V> = (
  item: SelectableListItem<T, V>,
  event: MouseEvent,
  index: number,
) => void

export type RenderItemProps<T, V extends string | number = string | number> = {
  itemElementProps: CellProps & { key?: string | number }
  isSelected: boolean
  item: SelectableListItem<T, V>
  button: JSX.Element
}

type Props<T, V extends string | number = string | number> = {
  name: string
  items?: Array<SelectableListItem<T, V>>
  selected?: Array<V>
  onItemClick?: ListItemClickCallback<T, V>
  isMultiSelect?: boolean
  renderItem?: (props: RenderItemProps<T, V>, index: number) => ReactNode
  itemButtonTestId?: string
  disableButtonClicks?: boolean
}

const isNotRootCatalog = (data: any): data is { code: string } => {
  return data && data.code !== WEB_CATALOG_ROOT_ALL_CODE
}

const hasBadgeProperty = (data: any): data is { badge: CatalogBadgeModel } => {
  return data && typeof data.badge !== 'undefined'
}

const renderBadge = (badge: CatalogBadgeModel): ReactNode => {
  if (!badge) return null

  return <Badge theme={badge.theme || 'primary'} content={badge.title} />
}

const SelectableItemList = <T, V extends string | number = string | number>({
  name,
  items = [],
  selected = [],
  onItemClick,
  isMultiSelect = false,
  renderItem,
  itemButtonTestId,
  disableButtonClicks = false,
}: Props<T, V>) => {
  const translate = useTranslate('catalog.filters')

  const handleItemClick =
    (item: SelectableListItem<T, V>, index: number) => (event: MouseEvent) => {
      if (!(event.target instanceof HTMLAnchorElement) && !disableButtonClicks) {
        event.preventDefault()
      }
      if (isFunction(onItemClick)) onItemClick(item, event, index)
    }

  const renderButton = (item: SelectableListItem<T, V>) => {
    const { value, data } = item
    const componentProps = {
      name: isMultiSelect ? `${name}[]` : name,
      value: String(value),
      checked: selected.includes(value),
      aria: {
        'aria-label': `${selected.includes(value) ? translate('a11y.actions.checked') : translate('a11y.actions.unchecked')}`,
      },
      tabIndex: -1,
      testId: itemButtonTestId && `${itemButtonTestId}-${item.id}`,
      onChange: () => undefined,
      onClick: (event: MouseEvent<HTMLInputElement>) => {
        if (disableButtonClicks) return
        event.stopPropagation()
      },
    }

    const badgeElement =
      hasBadgeProperty(data) && isNotRootCatalog(data) ? renderBadge(data.badge) : null
    let element = <Radio {...componentProps} />

    if (isMultiSelect) {
      element = <Checkbox {...componentProps} />
    }

    if (!disableButtonClicks) return element

    return (
      <Cell styling="tight" prefix={badgeElement}>
        <div className="u-no-pointer-events">{element}</div>
      </Cell>
    )
  }

  const renderListItem = (item: SelectableListItem<T, V>, index: number) => {
    const onClick = handleItemClick(item, index)
    const button = renderButton(item)

    const itemElementProps: CellProps & { key?: string | number } = {
      id: `${name}-list-item-${item.id}`,
      key: item.id,
      type: 'navigating',
      body: <Text text={item.title} type="title" as="span" />,
      onClick,
      suffix: button,
    }

    if (isFunction(renderItem)) {
      return renderItem(
        {
          itemElementProps,
          isSelected: selected.includes(item.value),
          item,
          button,
        },
        index,
      )
    }

    const { key, ...restItemElementProps } = itemElementProps

    return <Cell key={key} {...restItemElementProps} />
  }

  return <List>{items.map(renderListItem)}</List>
}

export default SelectableItemList
